import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { MessageSquare, Send, Loader2, Image as ImageIcon, ArrowDown, ArrowUp, AlertCircle, Zap, Sparkles, Settings as SettingsIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import api from '@/lib/api';
import logger from '@/lib/logger';
import { useAuth } from '@/contexts/AuthContext';
import { SmsTemplatesDialog } from '@/components/lead-detail/SmsTemplatesDialog';

const STATUS_COLORS = {
  delivered: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  sent: 'bg-sky-100 text-sky-800 border-sky-200',
  queued: 'bg-slate-100 text-slate-700 border-slate-200',
  received: 'bg-violet-100 text-violet-800 border-violet-200',
  failed: 'bg-red-100 text-red-700 border-red-200',
  undelivered: 'bg-red-100 text-red-700 border-red-200',
};

function MessageRow({ msg }) {
  const isOut = msg.direction === 'outbound';
  const Icon = isOut ? ArrowUp : ArrowDown;
  const statusClass = STATUS_COLORS[(msg.status || '').toLowerCase()] || STATUS_COLORS.queued;
  return (
    <div
      className={`rounded-sm border p-3 ${isOut ? 'bg-[#F8FAFF] border-[#0047AB]/15' : 'bg-violet-50/40 border-violet-200'}`}
      data-testid={`sms-row-${msg.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${isOut ? 'text-[#0047AB]' : 'text-violet-600'}`} />
          <span className="text-xs font-medium text-slate-700 truncate">
            {isOut ? (msg.sender_name || 'You') : 'Lead'}
          </span>
          {msg.channel === 'drip' && (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-amber-300 text-amber-700">
              drip{msg.drip_step ? `:${msg.drip_step}` : ''}
            </Badge>
          )}
          {msg.channel === 'bulk' && (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-slate-300 text-slate-600">
              bulk
            </Badge>
          )}
          {msg.is_mms && (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-pink-300 text-pink-700">
              MMS
            </Badge>
          )}
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded-sm border ${statusClass}`}>
          {msg.status || 'queued'}
        </span>
      </div>
      <p className="mt-2 text-sm text-slate-900 whitespace-pre-wrap break-words">{msg.body}</p>
      {msg.media_url && (
        <a
          href={msg.media_url}
          target="_blank"
          rel="noreferrer"
          className="mt-2 inline-flex items-center gap-1 text-xs text-[#0047AB] hover:underline"
        >
          <ImageIcon className="w-3 h-3" /> View media
        </a>
      )}
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-500">
        <span>{new Date(msg.created_at).toLocaleString()}</span>
        {msg.error_code && (
          <span className="inline-flex items-center gap-1 text-red-600">
            <AlertCircle className="w-3 h-3" /> error {msg.error_code}
          </span>
        )}
      </div>
    </div>
  );
}

export function LeadSmsPanel({ lead, isViewer }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState('');
  const [templates, setTemplates] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [showTemplateManager, setShowTemplateManager] = useState(false);

  useEffect(() => {
    api.getSmsTemplates()
      .then(r => setTemplates(r.data.templates || []))
      .catch(() => {});
  }, []);

  // Per-lead suggestions: templates whose match_statuses / match_categories
  // hit this lead's status / category. Empty response is fine — the
  // suggested row simply doesn't render.
  useEffect(() => {
    if (!lead?.id) return;
    api.suggestSmsTemplates(lead.id)
      .then(r => setSuggested(r.data.templates || []))
      .catch(() => setSuggested([]));
  }, [lead?.id, lead?.status, lead?.category]);

  /** Substitute template tokens against the current lead + user profile.
   *  Supported: {{first_name}}, {{name}}, {{booking_link}}, {{company}},
   *  {{status}}, {{owner_name}}. Leaves unknown tokens untouched. */
  const applyPlaceholders = (text) => {
    if (!text || !text.includes('{{')) return text;
    const firstName = (lead?.name || '').trim().split(/\s+/)[0] || 'there';
    const bookingLink = user?.booking_link || '';
    const company = lead?.company || '';
    const status = lead?.status || '';
    const ownerName = user?.name || '';
    return text
      .replace(/\{\{\s*first_name\s*\}\}/gi, firstName)
      .replace(/\{\{\s*name\s*\}\}/gi, lead?.name || firstName)
      .replace(/\{\{\s*booking_link\s*\}\}/gi, bookingLink)
      .replace(/\{\{\s*company\s*\}\}/gi, company)
      .replace(/\{\{\s*status\s*\}\}/gi, status)
      .replace(/\{\{\s*owner_name\s*\}\}/gi, ownerName);
  };
  const [mediaUrl, setMediaUrl] = useState('');
  const [showMms, setShowMms] = useState(false);
  const [sending, setSending] = useState(false);
  const [providerStatus, setProviderStatus] = useState(null);

  const fetchHistory = useCallback(() => {
    if (!lead?.id) return;
    setLoading(true);
    api.getLeadSmsHistory(lead.id)
      .then(r => setMessages(r.data.messages || []))
      .catch(err => logger.error(err))
      .finally(() => setLoading(false));
  }, [lead?.id]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  // Live-refresh conversation every 15s so returned texts appear
  // without a manual reload; also mark inbound as read on mount.
  // Skips polling when the tab is hidden to avoid background load.
  useEffect(() => {
    if (!lead?.id) return;
    api.markSmsConversationRead(lead.id).catch(() => {});
    const id = setInterval(() => {
      if (document.hidden) return;
      api.getLeadSmsHistory(lead.id)
        .then(r => setMessages(r.data.messages || []))
        .catch(() => {});
    }, 15000);
    return () => clearInterval(id);
  }, [lead?.id]);

  useEffect(() => {
    api.smsStatus()
      .then(r => setProviderStatus(r.data))
      .catch(() => setProviderStatus({ configured: false }));
  }, []);

  const send = async () => {
    if (!body.trim()) { toast.error('Message body is required'); return; }
    if (sending) return;
    setSending(true);
    try {
      await api.sendSmsToLead(lead.id, body.trim(), mediaUrl.trim() || null);
      toast.success(mediaUrl.trim() ? 'MMS queued' : 'SMS queued');
      setBody('');
      setMediaUrl('');
      setShowMms(false);
      fetchHistory();
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to send';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const hasPhone = !!(lead?.phone || '').trim();
  const disabled = isViewer || !hasPhone || !providerStatus?.configured;
  const charCount = body.length;

  return (
    <section className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6" data-testid="lead-sms-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-[#0047AB]" />
          <h2 className="text-base font-semibold text-slate-900">SMS / MMS</h2>
          {providerStatus?.configured && (
            <Badge variant="outline" className="text-[10px] py-0 px-1.5 border-emerald-300 text-emerald-700">
              ClickSend
            </Badge>
          )}
        </div>
        <span className="text-xs text-slate-500">{messages.length} message{messages.length === 1 ? '' : 's'}</span>
      </div>

      {!providerStatus?.configured && (
        <div className="mb-3 p-3 rounded-sm border border-amber-200 bg-amber-50 text-xs text-amber-800">
          ClickSend is not yet configured on this server. Add CLICKSEND_USERNAME / CLICKSEND_API_KEY in backend env.
        </div>
      )}
      {providerStatus?.configured && !hasPhone && (
        <div className="mb-3 p-3 rounded-sm border border-slate-200 bg-slate-50 text-xs text-slate-600">
          This lead has no phone number — add one to enable SMS.
        </div>
      )}

      {!isViewer && (
        <div className="space-y-2 mb-4">
          {suggested.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5" data-testid="sms-suggested-replies">
              <Sparkles className="w-3 h-3 text-[#0047AB] shrink-0" />
              <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-semibold mr-1">Suggested</span>
              {suggested.map(t => (
                <button
                  key={`sug-${t.id}`}
                  type="button"
                  onClick={() => setBody(applyPlaceholders(t.body))}
                  disabled={disabled}
                  className="text-[11px] font-medium px-2 py-1 rounded-sm bg-[#EFF6FF] hover:bg-[#dfe9fb] text-[#0047AB] transition-colors disabled:opacity-40"
                  title={t.body}
                  data-testid={`sms-suggested-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
          {templates.length > 0 && (
            <div className="flex items-center flex-wrap gap-1.5" data-testid="sms-quick-replies">
              <Zap className="w-3 h-3 text-[#FF7E0E] shrink-0" />
              {templates.map(t => (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => setBody(applyPlaceholders(t.body))}
                  disabled={disabled}
                  className="text-[11px] font-medium px-2 py-1 rounded-sm bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 transition-colors disabled:opacity-40"
                  title={t.body}
                  data-testid={`sms-template-${t.id}`}
                >
                  {t.label}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setShowTemplateManager(true)}
                className="text-[11px] text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 px-1.5 py-1 inline-flex items-center gap-1"
                data-testid="sms-templates-manage"
              >
                <SettingsIcon className="w-3 h-3" /> Manage
              </button>
            </div>
          )}
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder={hasPhone ? `Text ${lead.phone}…` : 'Add a phone number to text this lead'}
            rows={3}
            maxLength={1600}
            disabled={disabled}
            className="text-sm rounded-sm"
            data-testid="sms-compose-body"
          />
          {showMms && (
            <Input
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.target.value)}
              placeholder="https://… (image URL, JPG/PNG/GIF, ≤250 kB)"
              disabled={disabled}
              className="text-sm rounded-sm"
              data-testid="sms-compose-media"
            />
          )}
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 text-xs rounded-sm"
                onClick={() => setShowMms(v => !v)}
                disabled={disabled}
                data-testid="sms-toggle-mms-btn"
              >
                <ImageIcon className="w-3 h-3 mr-1" /> {showMms ? 'Drop media' : 'Attach media (MMS)'}
              </Button>
              <span className="text-[11px] text-slate-500">{charCount}/1600 · ~{Math.max(1, Math.ceil(charCount / 160))} part(s)</span>
            </div>
            <Button
              type="button"
              size="sm"
              className="h-8 text-xs rounded-sm bg-[#0047AB] hover:bg-[#003a8c]"
              onClick={send}
              disabled={disabled || sending || !body.trim()}
              data-testid="sms-send-btn"
            >
              {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Send
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="sms-history">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /></div>
        ) : messages.length === 0 ? (
          <p className="text-xs text-slate-500 py-4 text-center">No SMS history yet.</p>
        ) : (
          messages.map(m => <MessageRow key={m.id} msg={m} />)
        )}
      </div>
      <SmsTemplatesDialog
        open={showTemplateManager}
        onOpenChange={setShowTemplateManager}
        onChanged={setTemplates}
      />
    </section>
  );
}
